<template>
  <div v-if="item.showItem !== false && !item.hidden" class="menu-item">
    <div
      v-if="item.children"
      class="menu-dropdown"
      :class="{
        sideline: isActive && level == 1,
        sidedot: isActive && level >= 2,
      }"
    >
      <div
        class="menu-trigger navbar-link"
        :class="{
          nonactive: item.name === 'Shortcuts',
          'active-sub-menu': showSubmenu && level > 0,
          hover: level > 0,
          'current-route': isActive && level === 0,
        }"
        @click="toggleSubmenu"
      >
        <span
          :class="{
            'bold-text': isActive && level > 0,
          }"
          >{{ item.name }}</span
        >
        <span
          v-if="level > 0"
          class="material-icons icon-color"
          style="color: #7489a9"
          :class="{ 'active-sub-menu': showSubmenu && level > 0 }"
        >
          chevron_right
        </span>
      </div>

      <div
        v-show="showSubmenu"
        class="submenu"
        :class="getSubmenuClasses(level, actualDir)"
      >
        <PrimaryNavigationMenuItem
          v-for="(child, idx) in item.children"
          :key="`${level}-${idx}`"
          :item="child"
          :level="level + 1"
          :dir="actualDir"
          @close-all="closeAll"
        />
      </div>
    </div>

    <div v-else-if="item.isGroup" class="group" @click="toggleSubmenu">
      <span class="navbar-link group-title">{{ item.name }}</span>
      <PrimaryNavigationMenuItem
        v-for="(child, idx) in item.group"
        :key="`${level}-${idx}`"
        :item="child"
        :level="level"
        :dir="actualDir"
        @close-all="closeAll"
      />
    </div>

    <router-link
      v-else
      :to="{ name: item.route }"
      class="navbar-link"
      :class="{
        'current-route': isCurrentRoute && level === 0,
        'current-route-in-menu': isCurrentRoute && level !== 0,
        hover: level > 0,
        sideline: isCurrentRoute && level == 1,
        sidedot: isCurrentRoute && level > 1 && level < 3,
      }"
      @click.native="handleItemClick"
    >
      {{ item.name }}
    </router-link>
  </div>
</template>

<script>
export default {
  name: 'PrimaryNavigationMenuItem',
  props: {
    item: {
      type: Object,
      required: true
    },
    level: {
      type: Number,
      default: 0
    },
    dir: {
      type: String,
      default: 'right'
    },
    isParent: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showSubmenu: false,
      actualDir: this.dir
    }
  },
  computed: {
    isActive () {
      const isActive = this.checkActive(this.item)
      return isActive && this.item.name !== 'Shortcuts'
    },
    isCurrentRoute () {
      return this.$route.name === this.item.route || (this.item.also && this.$route.name === this.item.also)
    }
  },
  mounted () {
    document.addEventListener('click', this.handleOutsideClick)
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.handleOutsideClick)
    window.removeEventListener('resize', this.onWindowResize)
  },
  methods: {
    getSubmenuClasses (level, dir) {
      return {
        'submenu-below': level === 0,
        'submenu-left': level > 0 && dir === 'left',
        'submenu-right': level > 0 && dir === 'right'
      }
    },

    checkActive (item) {
      if ((item.route && this.$route.name === item.route) || (item.also && this.$route.name === item.also)) return true

      if (item.children) {
        const isActiveChild = item.children.some((c) => {
          if (c.route === this.$route.name) {
            return true
          }
        })

        return isActiveChild || item.children.some((c) => this.checkActive(c))
      }
      return false
    },
    toggleSubmenu () {
      if (!this.showSubmenu) {
        this.actualDir = this.dir
        this.showSubmenu = true
        this.$nextTick(() => {
          this.adjustSubmenuDirection()
        })
      } else {
        this.showSubmenu = false
      }
    },

    adjustSubmenuDirection () {
      const submenuEl = this.$el.querySelector('.submenu')
      const triggerEl = this.$el.querySelector('.menu-trigger')
      if (!submenuEl || !triggerEl) return

      const triggerRect = triggerEl.getBoundingClientRect()
      const vw = window.innerWidth
      const margin = 12

      const submenuWidth = submenuEl.offsetWidth

      let fitsPreferred = true
      if (this.dir === 'right') {
        const expectedRight = triggerRect.right + margin + submenuWidth
        fitsPreferred = expectedRight <= vw
      } else {
        const expectedLeft = triggerRect.left - margin - submenuWidth
        fitsPreferred = expectedLeft >= 0
      }

      if (fitsPreferred) {
        this.actualDir = this.dir
        return
      }

      const opposite = this.dir === 'right' ? 'left' : 'right'
      this.actualDir = opposite

      this.$nextTick(() => {
        const rect2 = submenuEl.getBoundingClientRect()
        if (rect2.left < 0 || rect2.right > vw) {
          const spaceRight = vw - triggerRect.left
          const spaceLeft = triggerRect.right
          this.actualDir = spaceRight >= spaceLeft ? 'right' : 'left'
        }
      })
    },

    handleOutsideClick (event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.showSubmenu = false
      }
    },

    onWindowResize () {
      if (this.showSubmenu) {
        window.clearTimeout(this._resizeTimeout)
        this._resizeTimeout = setTimeout(() => {
          this.adjustSubmenuDirection()
        }, 80)
      }
    },

    handleItemClick () {
      this.$emit('close-all')
    },

    closeAll () {
      this.showSubmenu = false
      this.$emit('close-all')
    }
  }
}
</script>

<style scoped lang="scss">
.sideline {
  position: relative;
  color: $iconColor !important;
  & .bold-text {
    color: $fontColorBase !important;
  }
}

.hover {
  transition: 0.2s;
}

.current-route {
  text-shadow: 0 0 0.5px currentColor, 0 0 0.5px currentColor;
  color: $fontColorAccent !important;
  background-color: none;
}

.current-route-in-menu {
  text-shadow: 0 0 0.5px currentColor, 0 0 0.5px currentColor;
  color: $iconColor !important;
  background-color: #e4efff;
}

.hover:hover {
  background-color: #eaedf4;
  color: $fontColorBase !important;
  text-shadow: 0 0 0.5px currentColor, 0 0 0.5px currentColor;
  .icon-color {
    color: $fontColorBase !important;
  }
}

.icon-color {
  color: $iconColor2;
}

.bold-text {
  text-shadow: 0 0 0.5px currentColor, 0 0 0.5px currentColor;
}

.sideline::before {
  position: absolute;
  content: "";
  height: 100%;
  width: 2px;
  left: 0px;
  top: 0px;
  background-color: $buttonBgColor1;
}

.group {
  border-top: 1px solid $tableBorderColor;
}

.group-title {
  font-weight: 600 !important;
  cursor: default !important;
  color: $body-color !important;
}

.menu-item {
  transition: all 0.2s;
  line-height: 24px;
}

.navbar-link {
  cursor: pointer;
  white-space: nowrap;
  color: $fontColorBase;
  font-size: 15px;
  text-decoration: none;
  display: inline-block;
  width: 100%;

  :global(.btn) {
    font-size: inherit;
  }
}

.navbar-link.active {
  color: $fontColorAccent !important;
  text-shadow: 0 0 0.5px currentColor, 0 0 0.5px currentColor;
}

.active {
  color: $iconColor !important;
  background-color: yellow;
  text-shadow: 0 0 0.5px currentColor, 0 0 0.5px currentColor;
}

.menu-dropdown {
  position: relative;
  display: block;
}

.active-sub-menu {
  color: $iconColor !important;
}

.sidedot {
  position: relative;
  transition: 0.2s;
  text-shadow: 0 0 0.5px currentColor, 0 0 0.5px currentColor;
  & .menu-trigger {
    padding-left: 24px !important;
  }
  & .bold-text {
    color: $fontColorBase !important;
  }
}

.navbar-link.sidedot {
  padding-left: 24px !important;
}

.sidedot::before {
  position: absolute;
  content: "";
  top: calc(100% - 24px);
  height: 8px;
  width: 8px;
  left: 9px;
  border-radius: 100%;
  z-index: 222;
  background-color: $buttonBgColor1;
  animation: fade 0.2s forwards;
}

.menu-trigger {
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.nonactive {
  color: inherit !important;
  font-weight: inherit !important;
  text-shadow: none !important;
}

.menu-trigger.active {
  text-shadow: 0 0 0.5px currentColor, 0 0 0.5px currentColor;
  color: $fontColorAccent;
}

.menu-arrow {
  font-size: 12px;
  transition: transform 0.2s ease;
}

.menu-arrow.rotated {
  transform: rotate(90deg);
}

.submenu {
  position: absolute;
  min-width: 100px;
  width: max-content;
  background: white;
  border-radius: 5px;
  box-shadow: 4px 4px 10px 0 rgba(208, 208, 208, 0.3);
  z-index: 1000;
  padding: 8px;
  border: 1px solid #F3F5F7;
  & .navbar-link,
  & .menu-trigger {
    color: $buttonTxtColor2;
    padding: 8px;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
  }
}

.submenu-below {
  top: 100%;
  left: 0;
  margin-top: 20px;
}

.submenu-right {
  top: -8px;
  left: 100%;
  margin-left: 12px;
}

.submenu-left {
  top: -8px;
  right: 100%;
  margin-right: 12px;
}
</style>
